import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Heart, ShoppingCart, X, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { formatPrice, calculateDiscount } from '../../utils/currency';
import { useToast } from '../../hooks/use-toast';

// name/description/shortDescription/highlights come back from the API as
// plain strings/arrays (one per country version — already resolved
// server-side), not `{es, en}` translation objects. Stay defensive in case
// a legacy shape ever shows up, but don't assume it.
const pickText = (value, lang) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value[lang] || value.es || value.en || Object.values(value)[0] || '';
  return '';
};

const QuickViewModal = ({ isOpen, onClose, product }) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // Price/stock live per size variant now. Non-sized products get exactly
  // one auto-created "Única" variant server-side, so auto-select it and
  // keep the selector hidden; real multi-size products start with nothing
  // selected — sizes can be priced/stocked differently, so we never guess.
  // Also reset per-product state whenever the modal is opened for a
  // (possibly different) product.
  useEffect(() => {
    if (!isOpen || !product) return;
    const variants = product.sizeVariants || [];
    setSelectedSize(variants.length === 1 ? variants[0].size : null);
    setSelectedImage(0);
    setQuantity(1);
  }, [isOpen, product?.id]);

  if (!product) return null;

  const productId = product._id || product.id;
  const isWishlisted = isInWishlist(productId);

  const currentLang = i18n.language;
  const productName = pickText(product.name, currentLang) || product.name;
  const productDescription = pickText(product.description, currentLang);
  const productShortDescription = pickText(product.shortDescription, currentLang);
  const productHighlights = Array.isArray(product.highlights) ? product.highlights : [];

  const sizeVariants = product.sizeVariants || [];
  const hasSizeChoice = sizeVariants.length > 1;
  const activeVariant = sizeVariants.find((sv) => sv.size === selectedSize) || null;

  // Before a size is chosen (only possible when there's more than one),
  // fall back to the product's derived summary price so the modal isn't
  // blank — stock/add-to-cart stay gated on a real choice.
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayCompareAtPrice = activeVariant ? activeVariant.compareAtPrice : product.compareAtPrice;
  const hasDiscount = displayCompareAtPrice && displayCompareAtPrice > displayPrice;
  const discountPercentage = hasDiscount
    ? calculateDiscount(displayCompareAtPrice, displayPrice)
    : 0;

  const inv = activeVariant?.inventory || {};
  const trackInventory = Boolean(inv.trackInventory);
  const allowBackorder = Boolean(inv.allowBackorder);
  const availableStock = !activeVariant
    ? 0
    : (!trackInventory || allowBackorder
        ? Infinity
        : Math.max(0, (inv.quantity ?? 0) - (inv.reserved ?? 0)));
  const isOutOfStock = hasSizeChoice
    ? Boolean(activeVariant) && availableStock === 0
    : availableStock === 0;

  const images = product.images || [{ medium: product.image }];

  const handleAddToCart = async () => {
    if (isAddingToCart) return;
    if (hasSizeChoice && !selectedSize) {
      toast({
        title: t('quickViewExtra.selectSize'),
        variant: 'destructive'
      });
      return;
    }
    // CartContext handles its own toasts (success + error) and never throws.
    // Only close the modal and reset quantity if the item was actually added.
    setIsAddingToCart(true);
    try {
      const ok = await addToCart(productId, quantity, selectedSize);
      if (ok) {
        setQuantity(1);
        onClose();
      }
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleToggleWishlist = async () => {
    try {
      await toggleWishlist(productId);
      toast({
        title: t('common.success'),
        description: isWishlisted ? t('quickViewMore.removedFromWishlist') : t('quickViewMore.addedToWishlist')
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.message || t('quickViewMore.updateWishlistFailed'),
        variant: 'destructive'
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!max-w-[800px] w-[95vw] max-h-[90vh] overflow-y-auto p-0">
        <VisuallyHidden>
          <DialogTitle>{productName}</DialogTitle>
          <DialogDescription>
            {t('quickViewMore.dialogDescription', { name: productName, interpolation: { escapeValue: false } })}
          </DialogDescription>
        </VisuallyHidden>
        <div className="grid md:grid-cols-2 gap-6 p-6">
          {/* Left: Images */}
          <div className="flex flex-col h-full max-h-[70vh]">
            {/* Main Image */}
            <div className="relative w-full h-full max-h-[60vh] bg-gray-100 rounded-lg overflow-hidden mb-4 flex items-center justify-center">
              <img
                src={images[selectedImage]?.medium || images[selectedImage]?.original}
                alt={productName}
                className="max-w-full max-h-full w-auto h-auto object-contain"
              />

              {/* Badges */}
              <div className="absolute top-2 left-2 flex flex-col gap-2">
                {product.isNew && (
                  <Badge className="bg-[#2C2420] text-white">{t('quickViewMore.newBadge')}</Badge>
                )}
                {hasDiscount && (
                  <Badge className="bg-[#be1f1a] text-white">
                    {discountPercentage}% {t('products.off')}
                  </Badge>
                )}
              </div>

              {/* ReBorn badge — subtle mark for pieces made from reclaimed
                  materials. Links through to the explainer page (closing the
                  modal first, same as the "Ver Todos los Detalles" link below). */}
              {product.isReborn && (
                <Link
                  to="/reborn"
                  className="absolute bottom-2 right-2 z-10"
                  onClick={onClose}
                >
                  <img
                    src="/reborn-logo.png"
                    alt={t('products.rebornBadge')}
                    title={t('products.rebornBadge')}
                    className="w-[72px] h-auto rounded-md bg-[#FEFCF7] px-1.5 py-1 shadow-sm"
                    data-testid="quickview-reborn-badge"
                  />
                </Link>
              )}
            </div>

            {/* Thumbnail Images */}
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === index
                        ? 'border-[#be1f1a]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={img.thumbnail || img.medium}
                      alt={`${productName} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Product Info */}
          <div className="flex flex-col">
            {/* Category */}
            {product.category && (
              <Link
                to={`/shop/${product.categorySlug || product.category.toLowerCase()}`}
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-[#be1f1a] transition-colors uppercase tracking-wide mb-2"
              >
                {product.category}
              </Link>
            )}

            {/* Product Name */}
            <h2 className="text-2xl font-semibold text-[#2C2420] mb-3">
              {productName}
            </h2>

            {/* Price */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl font-bold text-[#be1f1a]">
                {formatPrice(displayPrice)}
              </span>
              {hasDiscount && (
                <span className="text-xl text-gray-400 line-through">
                  {formatPrice(displayCompareAtPrice)}
                </span>
              )}
            </div>

            {/* Description */}
            {(productShortDescription || productDescription) && (
              <div className="mb-4">
                {productShortDescription && (
                  <p className="text-sm italic text-gray-700 mb-1">
                    {productShortDescription}
                  </p>
                )}
                {productDescription && productDescription !== productShortDescription && (
                  <p className="text-sm text-gray-600 max-h-32 overflow-y-auto">
                    {productDescription}
                  </p>
                )}
              </div>
            )}

            {/* Highlights */}
            {productHighlights.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold mb-2">{t('products.highlights')}</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {productHighlights.slice(0, 3).map((highlight, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#be1f1a] mr-2">•</span>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Size — multiple sizes get a selector; a single real size
                (anything but the non-sized "Única" sentinel) still gets a
                static label so the shopper knows exactly what they're
                buying, even though there's nothing to choose. */}
            {hasSizeChoice ? (
              <div className="mb-4">
                <h4 className="text-sm font-semibold mb-2">
                  {t('products.size')}
                  {activeVariant && (
                    <span className="ml-2 font-normal text-gray-500">— {activeVariant.size}</span>
                  )}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {sizeVariants.map((sv) => {
                    const svInv = sv.inventory || {};
                    const svStock = !svInv.trackInventory || svInv.allowBackorder
                      ? Infinity
                      : Math.max(0, (svInv.quantity ?? 0) - (svInv.reserved ?? 0));
                    const svUnavailable = sv.isAvailable === false || svStock === 0;
                    const isSelected = selectedSize === sv.size;
                    return (
                      <button
                        key={sv.size}
                        type="button"
                        onClick={() => {
                          if (svUnavailable) return;
                          setSelectedSize(sv.size);
                          setQuantity(1);
                        }}
                        disabled={svUnavailable}
                        title={svUnavailable ? t('products.outOfStock') : undefined}
                        className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors ${
                          isSelected
                            ? 'border-[#be1f1a] bg-[#be1f1a] text-white'
                            : svUnavailable
                              ? 'border-gray-200 text-gray-300 line-through cursor-not-allowed'
                              : 'border-gray-300 hover:border-[#be1f1a] text-[#2C2420]'
                        }`}
                      >
                        {sv.size}
                      </button>
                    );
                  })}
                </div>
                {!activeVariant && (
                  <p className="text-sm text-gray-500 mt-2">
                    {t('quickViewExtra.selectSize')}
                  </p>
                )}
              </div>
            ) : (
              activeVariant && activeVariant.size && activeVariant.size !== 'Única' && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold">
                    {t('products.size')}
                    <span className="ml-2 font-normal text-gray-500">{activeVariant.size}</span>
                  </h4>
                </div>
              )
            )}

            {/* Stock status for the resolved size */}
            {activeVariant && (
              <div className="mb-4">
                {isOutOfStock ? (
                  <p className="text-sm text-red-600 font-medium">{t('products.outOfStock')}</p>
                ) : trackInventory && !allowBackorder && Number.isFinite(availableStock) ? (
                  <p className="text-sm text-green-600 font-medium">
                    ✓ {t('products.inStock')} ({availableStock} {t('quickViewExtra.available')})
                  </p>
                ) : (
                  <p className="text-sm text-green-600 font-medium">✓ {t('products.inStock')}</p>
                )}
              </div>
            )}

            {/* Quantity */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-2">{t('products.quantity')}:</h4>
              <div className="flex items-center border rounded-lg w-32">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 hover:bg-gray-50 transition-colors"
                >
                  -
                </button>
                <span className="flex-1 text-center font-medium">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-2 hover:bg-gray-50 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mb-4">
              <Button
                onClick={handleAddToCart}
                disabled={isAddingToCart || isOutOfStock}
                data-testid="quickview-add-to-cart"
                className="flex-1 bg-[#be1f1a] hover:bg-[#a71b17] disabled:bg-gray-300 disabled:cursor-not-allowed text-white h-12"
              >
                {isAddingToCart ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : isOutOfStock ? (
                  t('products.outOfStock')
                ) : (
                  <>
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    {t('products.addToCart')}
                  </>
                )}
              </Button>
              <Button
                onClick={handleToggleWishlist}
                variant="outline"
                size="icon"
                className="h-12 w-12 border-2"
              >
                <Heart
                  className={`h-5 w-5 transition-colors ${
                    isWishlisted ? 'fill-[#be1f1a] text-[#be1f1a]' : 'text-gray-600'
                  }`}
                />
              </Button>
            </div>

            {/* View Full Details Link */}
            <Link
              to={`/product/${product.slug}`}
              onClick={onClose}
              className="text-center text-sm text-[#be1f1a] hover:underline"
            >
              {t('products.viewFullDetails')} →
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickViewModal;
